/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ButtonGroup from '../../../../client/js/components/form/ButtonGroup'
import FormSaveButton from '../../../../client/js/components/form/buttons/FormSaveButton'
import SmartForm from '../../../../client/js/components/form/SmartForm'
import RecordMetas from '../../../../client/js/components/RecordMetas'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../../client/js/lib/form'
import { AuthContext } from '../../../auth/components/AuthProvider'
import { fetchCustomer } from '../../lib/rest/customerAPIClient'
import { patchProspecting, postProspecting } from '../../lib/rest/prospectingAPIClient'
import ProspectingSchema from '../../schemas/ProspectingSchema.mjs'
import BedroomsField from '../fields/BedroomsField'
import CustomerField from '../fields/CustomerField'
import EquippedField from '../fields/EquippedField'
import EstateTypesField from '../fields/EstateTypesField'
import FurnishedField from '../fields/FurnishedField'
import MaxPriceField from '../fields/MaxPriceField'
import MinPriceField from '../fields/MinPriceField'
import NoteField from '../fields/NoteField'
import TransactionField from '../fields/TransactionField'
import FormSection from '../FormSection'
import FamilySituationLabel from '../labels/FamilySituationLabel'
import FundingMethodLabel from '../labels/FundingMethodLabel'
import NoSensibleInfoHere from '../NoSensibleInfoHere'
import LabelledValue from '../LabelledValue'
import ProspectingResultList from './ProspectingResultList'
import { useQuery } from '@tanstack/react-query'
import CitiesInput from '../inputs/CitiesInput'
import { queryClient } from '../../../../client/js/lib/query'
import { getProspectingListLink } from '../../lib/links/prospectingLinks'
import FormResetButton from '../../../../client/js/components/form/buttons/FormResetButton'
import { Box, Group, Radio, SimpleGrid, Stack } from '@mantine/core'
import useCustomForm from '../../../../client/js/hooks/useCustomForm'
import { parseBoolean } from '@jalik/form-parser'

export type ProspectingFormProps = {
  initialValues: Partial<Prospecting>,
  redirectUrl?: string
}

function ProspectingForm (props: ProspectingFormProps) {
  const {
    initialValues,
    redirectUrl = getProspectingListLink({ succeeded: 'null' })
  } = props

  const intl = useIntl()
  const auth = useContext(AuthContext)
  const id = initialValues ? initialValues.id : null
  const editable = auth.hasPermission('write:prospecting')

  const form = useCustomForm({
    disabled: !editable,
    initialValues: useMemo<Partial<Prospecting>>(() => ({
      succeeded: null,
      ...initialValues
    }), [initialValues]),
    initializeField: createFieldInitializer(ProspectingSchema),
    validate: createFormValidator(ProspectingSchema, { removeUnknown: true }),
    validateField: createFieldValidator(ProspectingSchema),
    onSubmit: (submitValues) => {
      const finalValues = removeUnknownFields(submitValues, ProspectingSchema)
      return id > 0
        ? patchProspecting(id, finalValues)
        : postProspecting(finalValues)
    },
    onSuccess () {
      queryClient.invalidateQueries({ queryKey: ['prospecting', id] })
      queryClient.invalidateQueries({ queryKey: ['prospecting'] })
    }
  })
  const { setValue, values } = form

  const customerQuery = useQuery({
    enabled: values.customerId != null,
    queryKey: ['customer', values.customerId],
    queryFn: () => fetchCustomer(values.customerId),
    refetchOnWindowFocus: false
  })
  const { data: customer } = customerQuery

  const handleCitiesChange = useCallback((citiesIds: string[]) => {
    setValue('cities', citiesIds.map((cityId) => ({
      cityId: parseInt(cityId, 10),
      prospectingId: values.id
    })))
  }, [setValue, values.id])

  return (
    <SmartForm
      context={form}
      redirectUrl={id == null ? redirectUrl : undefined}
    >
      <Stack>
        {initialValues
          ? <RecordMetas metas={initialValues} />
          : null}

        <FormSection
          defaultOpened
          title={intl.formatMessage({
            id: 'general',
            defaultMessage: 'Général'
          })}
        >
          <SimpleGrid cols={{ lg: 2 }}>
            <Stack gap="xs">
              <Radio.Group
                label={intl.formatMessage({
                  id: 'prospecting_status',
                  defaultMessage: 'État de la prospection'
                })}
                {...form.getFieldProps('succeeded', null, { parse: parseBoolean })}
              >
                <Group gap="xs">
                  <Radio
                    label={intl.formatMessage({
                      id: 'ongoing',
                      defaultMessage: 'En cours'
                    })}
                    value=""
                  />
                  <Radio
                    label={intl.formatMessage({
                      id: 'success',
                      defaultMessage: 'Succès'
                    })}
                    value="true"
                  />
                  <Radio
                    label={intl.formatMessage({
                      id: 'failure',
                      defaultMessage: 'Échec'
                    })}
                    value="false"
                  />
                </Group>
              </Radio.Group>

              <CustomerField disabled={!!id || initialValues.customerId != null} />

              {customer
                ? <Box fz="sm">
                  <LabelledValue
                    label={intl.formatMessage({
                      id: 'profession',
                      defaultMessage: 'Profession'
                    })}
                    value={customer.profession}
                  />
                  <LabelledValue
                    label={intl.formatMessage({
                      id: 'family_situation',
                      defaultMessage: 'Situation familiale'
                    })}
                    value={
                      <FamilySituationLabel value={customer.familySituation} />}
                  />
                  <LabelledValue
                    label={intl.formatMessage({
                      id: 'funding_method',
                      defaultMessage: 'Mode de financement'
                    })}
                    value={<FundingMethodLabel value={customer.fundingMethod} />}
                  />
                </Box>
                : null}
            </Stack>

            <NoteField
              rows={10}
              description={<NoSensibleInfoHere />}
            />
          </SimpleGrid>
        </FormSection>

        <FormSection
          defaultOpened
          title={intl.formatMessage({
            id: 'search_criteria',
            defaultMessage: 'Critères de recherche'
          })}
        >
          <SimpleGrid cols={{ sm: 2, lg: 4 }}>
            <TransactionField />
            <EstateTypesField />
            <FurnishedField nullable />
            <EquippedField nullable />
            <MinPriceField />
            <MaxPriceField />
            <CitiesInput
              onChange={handleCitiesChange}
              value={values.cities?.map(({ cityId }) => String(cityId))}
            />
            <BedroomsField />
          </SimpleGrid>
        </FormSection>

        <ProspectingResultList prospecting={form.values} />

        {editable
          ? <ButtonGroup>
            <FormResetButton />
            <FormSaveButton />
          </ButtonGroup>
          : null}
      </Stack>
    </SmartForm>
  )
}

export default ProspectingForm
